/**
 * Mobile App Section Component
 * Continuous marquee advert carousel with golden download banner
 * Data fetched from CMS API
 */

import React, { useState, useEffect } from 'react';
import { Download, Smartphone } from 'lucide-react';
import axios from 'axios';
import { sanitizeHtml } from '../../../utils/sanitizeHtml';

const CMS_API_URL = process.env.REACT_APP_API_BASE_URL_CMS || '';

const ADVERT_CATEGORY_ID = 36;
const MOBILE_APP_UID = 'MOBILE-APP';

const MobileAppSection = () => {
  const [adverts, setAdverts] = useState([]);

  const [mobileAppContent, setMobileAppContent] = useState({
    Title: '',
    SubTitle: '',
    ButtonText: '',
    Description: '',
    Link: '',
  });

  const [loading, setLoading] = useState(true);

  /**
   * Fetch adverts
   */
  useEffect(() => {
    const fetchAdverts = async () => {
      try {
        const response = await axios.post(
          `${CMS_API_URL}info/by-category`,
          {
            CategoryId: ADVERT_CATEGORY_ID,
          },
          {
            headers: {
              'Content-Type': 'application/json',
            },
            timeout: 30000,
          }
        );

        if (
          response.data?.code === 1 &&
          Array.isArray(response.data?.data)
        ) {
          // Only published and non-deleted adverts
          const filteredItems = response.data.data.filter(
            (item) =>
              item.IsDeleted === false &&
              item.IsPublished === true
          );

          const apiAdverts = filteredItems.map((item, index) => ({
            id: item.InfoId || index,
            image: item.ImageUrl || item.Image || '',
            alt: item.Title || `Advertisement ${index + 1}`,
          }));

          setAdverts(apiAdverts);
        }
      } catch (error) {
        console.error('Failed to fetch adverts:', error);
      }
    };

    fetchAdverts();
  }, []);

  /**
   * Fetch mobile app section content
   */
  useEffect(() => {
    const fetchMobileAppContent = async () => {
      try {
        const response = await axios.post(
          `${CMS_API_URL}sections/get-by-uid`,
          {
            UID: MOBILE_APP_UID,
          },
          {
            headers: {
              'Content-Type': 'application/json',
            },
            timeout: 30000,
          }
        );

        if (
          response.data?.code === 1 &&
          Array.isArray(response.data?.data) &&
          response.data.data.length > 0
        ) {
          const data = response.data.data[0];

          // Only published and non-deleted content
          if (
            data.IsDeleted === false &&
            data.IsPublished === true
          ) {
            setMobileAppContent({
              Title: data.Title || '',
              SubTitle: data.SubTitle || '',
              ButtonText: data.ButtonText || 'Download App',
              Description: data.Description || '',
              Link: data.Link || '',
            });
          }
        }
      } catch (error) {
        console.error(
          'Failed to fetch mobile app content:',
          error
        );
      } finally {
        setLoading(false);
      }
    };

    fetchMobileAppContent();
  }, []);

  /**
   * Duplicate adverts for infinite marquee
   */
  const duplicatedAdverts = [...adverts, ...adverts];

  /**
   * Download button action
   */
  const handleDownloadClick = () => {
    if (mobileAppContent.Link) {
      window.open(
        mobileAppContent.Link,
        '_blank',
        'noopener,noreferrer'
      );
    }
  };

  /**
   * Loading skeleton
   */
  if (loading) {
    return (
      <section className="pt-0 pb-8 md:pb-0 bg-white">
        <div className="container mx-auto px-6 md:px-8 lg:px-12">
          <div className="relative overflow-hidden">
            <div className="flex">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="w-[260px] sm:w-[300px] md:w-[360px] lg:w-[420px] flex-shrink-0 px-2"
                >
                  <div className="h-[160px] sm:h-[180px] md:h-[200px] bg-gray-200 animate-pulse" />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-[#ca9a37] border-t-2 border-[#2f3951] mt-4">
          <div className="container mx-auto px-6 md:px-8 lg:px-12 py-2 md:py-3">
            <div className="h-12 bg-white/20 rounded animate-pulse" />
          </div>
        </div>
      </section>
    );
  }

  /**
   * Hide component if no data
   */
  if (
    adverts.length === 0 &&
    !mobileAppContent.Title
  ) {
    return null;
  }

  return (
    <section className="pt-0 pb-8 md:pb-0 bg-white">
      {/* ============================= */}
      {/* Marquee Advert Carousel */}
      {/* ============================= */}

      <div className="container mx-auto px-6 md:px-8 lg:px-12">
        <div className="relative overflow-hidden">
          {/* Left overlay */}
          <div className="pointer-events-none absolute inset-y-0 left-0 w-12 md:w-16 lg:w-20 bg-gradient-to-r from-white via-white/90 to-transparent z-10" />

          {/* Right overlay */}
          <div className="pointer-events-none absolute inset-y-0 right-0 w-12 md:w-16 lg:w-20 bg-gradient-to-l from-white via-white/90 to-transparent z-10" />

          <div className="flex animate-marquee">
            {duplicatedAdverts.map((advert, index) => (
              <div
                key={`${advert.id}-${index}`}
                className="w-[260px] sm:w-[300px] md:w-[360px] lg:w-[420px] flex-shrink-0 px-2"
              >
                <div className="relative overflow-hidden shadow-lg">
                  <img
                    src={advert.image}
                    alt={advert.alt}
                    className="w-full h-[160px] sm:h-[180px] md:h-[200px] object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ============================= */}
      {/* Golden Download Banner */}
      {/* ============================= */}

      <div className="bg-[#ca9a37] border-t-2 border-[#2f3951]">
        <div className="container mx-auto px-6 md:px-8 lg:px-12 py-3">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            {/* Left Content */}
            <div className="flex items-center gap-3 md:gap-4">
              <Smartphone className="w-7 h-7 md:w-8 md:h-8 text-[#2f3951]" />

              <div>
                {/* Title */}
                <h3
                  className="text-2xl md:text-3xl lg:text-4xl text-[#2f3951] leading-snug"
                  style={{
                    fontFamily:
                      "'Edwardian Script ITC', 'Brush Script MT', 'Snell Roundhand', cursive",
                  }}
                >
                  {mobileAppContent.Title}{' '}
                  <span className="text-white">
                    {mobileAppContent.SubTitle}
                  </span>
                </h3>
              </div>
            </div>

            {/* Download Button */}
            <div className="flex flex-col items-center gap-1">
              <button
                onClick={handleDownloadClick}
                className="flex-shrink-0 bg-white hover:bg-gray-50 text-[#2f3951] font-semibold text-sm px-5 py-2 rounded-lg flex items-center transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#ca9a37]"
              >
                <Download className="w-4 h-4 mr-2" />

                {mobileAppContent.ButtonText ||
                  'Download App'}
              </button>

              {mobileAppContent.Description && (
                <div
                  className="text-xs md:text-sm text-[#2f3951] mt-1 font-medium"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(mobileAppContent.Description),
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MobileAppSection;